import React, { useEffect, useMemo, useRef, useState } from 'react';
import ipc from '../services/ipc';
import { BATPAC_TEMPLATES, BATPAC_FAMILIES } from '../services/templateCatalog';
import {
  buildConfigurationProject,
  summarizeProject,
  materialParamKeys,
  cleanMaterialValues,
  templateConfigurations,
  percentChange,
} from '../services/batpacDesign';
import { getDesignParameters } from '../utils/designParameters';
import SourcesButton from './SourcesButton';
import './BatpacDesigner.css';

const DEFAULT_KEY = 'nmc811';

const fmt = (value, digits) => (Number.isFinite(value) ? value.toFixed(digits) : '—');

/** "+4%" / "−3%" / "same", coloured by whether the change is good. */
function Delta({ value, baseline, higherIsBetter }) {
  const pct = percentChange(value, baseline);
  if (pct === null) return null;
  if (Math.abs(pct) < 0.5) return <span className="bp-delta">same</span>;
  const good = higherIsBetter ? pct > 0 : pct < 0;
  const sign = pct > 0 ? '+' : '−';
  return (
    <span className={`bp-delta ${good ? 'bp-delta--good' : 'bp-delta--bad'}`}>
      {sign}{Math.abs(pct).toFixed(0)}%
    </span>
  );
}

function Metrics({ summary, baseline, isBaseline }) {
  if (!summary) return <div className="bp-metrics bp-metrics--empty">No result</div>;
  const rows = [
    { key: 'whPerKg', value: fmt(summary.whPerKg, 0), unit: 'Wh/kg', higherIsBetter: true },
    { key: 'carbon', value: fmt(summary.carbon, 1), unit: 'kg CO₂e/kWh', higherIsBetter: false },
    { key: 'cost', value: fmt(summary.cost, 1), unit: '$/kWh', higherIsBetter: false },
  ];
  return (
    <dl className="bp-metrics">
      {rows.map((r) => (
        <div key={r.key} className="bp-metric">
          <dt>{r.unit}</dt>
          <dd>
            <span className="bp-metric-value">{r.value}</span>
            {isBaseline && <span className="bp-delta" aria-hidden="true" />}
            {baseline && !isBaseline && <Delta value={summary[r.key]} baseline={baseline[r.key]} higherIsBetter={r.higherIsBetter} />}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * "Design a process template with BatPaC": pick a cathode, optionally type
 * your own cathode numbers, compare BatPaC's cell designs for that cathode, and
 * start a project from one. Changes are against the cathode's energy cell at
 * BatPaC's values. All numbers are computed here, live, from the cathode's
 * BatPaC process template (see services/batpacDesign.js).
 *
 * Props:
 *   onBack()                               – back to the start screen
 *   onUse({ template, entry, config, material }) – start a project from a design
 */
export default function BatpacDesigner({ onBack, onUse }) {
  const [key, setKey] = useState(DEFAULT_KEY);
  const [material, setMaterial] = useState({});
  const [templates, setTemplates] = useState({});
  const [loadErrors, setLoadErrors] = useState({});
  const [attempt, setAttempt] = useState(0);
  const requested = useRef(new Set());

  const entry = BATPAC_TEMPLATES.find((t) => t.key === key);

  // Load each picked cathode's template once. A failed load is forgotten so
  // picking it again (or Try again) retries it.
  useEffect(() => {
    const t = entry;
    if (!t || requested.current.has(t.key)) return;
    requested.current.add(t.key);
    setLoadErrors(({ [t.key]: _, ...rest }) => rest);
    ipc.invoke('load-template', t.template, { builtin: true })
      .then((tpl) => setTemplates((prev) => ({ ...prev, [t.key]: tpl })))
      .catch((err) => {
        requested.current.delete(t.key);
        setLoadErrors((prev) => ({
          ...prev,
          [t.key]: `Could not load the ${t.label} process template: ${err.message}`,
        }));
      });
  }, [entry, attempt]);

  const error = loadErrors[key] || null;

  const template = templates[key];
  const block = useMemo(() => (template ? getDesignParameters(template) : null), [template]);
  const materialKeys = block ? materialParamKeys(block) : [];
  const own = block ? cleanMaterialValues(block, material) : {};
  const hasOwn = Object.keys(own).length > 0;

  const pickCathode = (next) => {
    setKey(next);
    setMaterial({});
  };

  // The reference: this cathode's energy cell at BatPaC's values.
  const baseline = useMemo(() => (template ? summarizeProject(buildConfigurationProject(template)) : null), [template]);

  // `own` is a new object every render; its JSON changes only when a value does.
  const ownKey = JSON.stringify(own);
  const cards = useMemo(() => {
    if (!template) return [];
    const values = JSON.parse(ownKey);
    return templateConfigurations(template).map((config) => ({
      config,
      summary: summarizeProject(buildConfigurationProject(template, config, values)),
    }));
  }, [template, ownKey]);

  const standIn = template?.batpac?.stand_in;
  // Without the user's own values the energy card is the reference itself.
  const isBaselineCard = (config) => !hasOwn && config.id === 'energy';

  const materialRows = materialKeys.map((k) => {
    const def = block.params[k];
    const raw = material[k] ?? '';
    const typed = raw !== '';
    const valid = k in own;
    return { k, def, raw, typed, valid };
  });

  return (
    <div className="bp-screen">
      <button type="button" className="bp-back" onClick={onBack}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polyline points="15 18 9 12 15 6" />
        </svg>
        Back
      </button>
      <h2 className="bp-title">
        Design a process template with BatPaC
        {block && (
          <SourcesButton
            sources={block.sources}
            notes={[
              'Cost is materials and energy only.',
              standIn && `${entry.label} is scored as ${standIn.label} cathode powder. ${standIn.why}`,
            ]}
          />
        )}
      </h2>

      {error && (
        <div className="bp-error" role="alert">
          {error}
          <button type="button" className="bp-retry" onClick={() => setAttempt((n) => n + 1)}>
            Try again
          </button>
        </div>
      )}

      <section className="bp-step" aria-labelledby="bp-step-cathode">
        <h3 id="bp-step-cathode">1. Cathode</h3>
        <div className="bp-families">
          {BATPAC_FAMILIES.map((family) => (
            <div key={family} className="bp-family">
              <span className="bp-family-name">{family}</span>
              <div className="bp-chips" role="group" aria-label={family}>
                {BATPAC_TEMPLATES.filter((t) => t.family === family).map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    className={`bp-chip${t.key === key ? ' bp-chip--on' : ''}`}
                    aria-pressed={t.key === key}
                    onClick={() => pickCathode(t.key)}
                    title={t.standIn ? `Scored as ${t.standIn} cathode powder` : undefined}
                  >
                    {t.label}
                    {t.standIn && <span className="bp-chip-mark" aria-label="stand-in">*</span>}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="bp-step" aria-labelledby="bp-step-material">
        <h3 id="bp-step-material">2. Your cathode's numbers <span className="bp-optional">optional</span></h3>
        <p className="bp-hint">Testing your own material? Enter its numbers. Blank keeps BatPaC's.</p>
        <div className="bp-material">
          {materialRows.map(({ k, def, raw, typed, valid }) => (
            <label key={k} className="bp-material-row">
              <span className="bp-material-label">{def.label}</span>
              <input
                type="number"
                inputMode="decimal"
                min={def.min}
                max={def.max}
                step="any"
                value={raw}
                placeholder={String(def.default)}
                onChange={(e) => setMaterial((prev) => ({ ...prev, [k]: e.target.value }))}
                aria-invalid={typed && !valid}
              />
              <span className="bp-material-unit">{def.unit}</span>
              <span className={`bp-tag ${valid ? 'bp-tag--yours' : 'bp-tag--batpac'}`}>
                {valid ? 'yours' : 'BatPaC'}
              </span>
              {typed && !valid && (
                <span className="bp-material-error">Use {def.min}–{def.max} {def.unit}</span>
              )}
            </label>
          ))}
        </div>
      </section>

      <section className="bp-step" aria-labelledby="bp-step-design">
        <h3 id="bp-step-design">3. Pick a cell design</h3>
        {baseline && (
          <p className="bp-hint">
            {hasOwn
              ? <>% vs BatPaC's {entry.label} energy cell: {fmt(baseline.whPerKg, 0)} Wh/kg · {fmt(baseline.carbon, 1)} kg CO₂e/kWh · ${fmt(baseline.cost, 1)}/kWh</>
              : '% vs the energy cell'}
          </p>
        )}

        <div className="bp-cards" data-coach="batpac-designs">
          {cards.map(({ config, summary }) => (
            <article key={config.id} className="bp-card">
              <h4>{entry.label} {config.label.toLowerCase()}{hasOwn ? ', your cathode' : ''}</h4>
              <p className="bp-card-desc">{config.description}</p>
              <Metrics summary={summary} baseline={baseline} isBaseline={isBaselineCard(config)} />
              <button
                type="button"
                className="bp-use"
                disabled={!summary}
                onClick={() => onUse({ template, entry, config, material: own })}
              >
                Use this one
              </button>
            </article>
          ))}
          {!template && !loadErrors[key] && <p className="bp-hint">Loading {entry?.label}…</p>}
        </div>
      </section>
    </div>
  );
}
